import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, isSignal, TemplateRef, WritableSignal } from '@angular/core';

import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';

import { SuggestionComponent, SuggestionData } from './suggestion.component';

export interface SuggestionsModalData {
  description?: string | TemplateRef<unknown>;
  suggestedSteps: SuggestionData[];
}

@Component({
  imports: [IconComponent, SuggestionComponent, NgTemplateOutlet],
  template: `
    <div class="flex w-screen max-w-[435px] flex-col gap-[10px]">
      @if (data.description) {
        @if (descriptionIsTemplate) {
          <ng-container *ngTemplateOutlet="dscriptionTemplate"></ng-container>
        } @else {
          <div [innerHtml]="data.description" class="mb-[5px]"></div>
        }
      }
      <div class="flex items-center">
        <aux-icon name="AuxLogo" class="mr-2" />
        <div class="font-semibold">Suggested Next Steps</div>
      </div>
      @for (item of data.suggestedSteps; track item.name) {
        @if (!itemHidden(item)) {
          <aux-suggestion [data]="item" (action)="onAction(item)" />
        }
      }
      @if (allSuggestionsHidden()) {
        <div class="flex h-[100px] items-center justify-center text-xl font-semibold">
          No suggested steps
        </div>
      }
    </div>
  `,
})
export class SuggestionsConfirmationModalComponent {
  readonly ref = inject(CustomOverlayRef);

  allSuggestionsHidden = computed(() =>
    this.data.suggestedSteps.every((item) => this.itemHidden(item))
  );

  get data(): SuggestionsModalData {
    return this.ref.data || { suggestedSteps: [] };
  }

  get descriptionIsTemplate(): boolean {
    return this.data.description instanceof TemplateRef;
  }

  get dscriptionTemplate(): TemplateRef<unknown> {
    return this.data.description as TemplateRef<unknown>;
  }

  itemHidden(data: SuggestionData): boolean {
    if (isSignal(data.hidden)) {
      return (data.hidden as WritableSignal<boolean>)();
    }

    return !!data.hidden;
  }

  async onAction(data: SuggestionData): Promise<void> {
    await data.action?.();
    this.ref.close();
  }
}
